import { Fragment, type ReactNode } from "react";

import { cn } from "@/lib/utils";

import {
  MarketingSection,
  SectionIntro,
  SectionKicker,
  SectionTitle,
} from "./MarketingSection";

export type Step = { title: string; body: ReactNode };

const CARD_STYLES = [
  {
    card: "bg-[linear-gradient(145deg,#fff_30%,#eef0ff)] after:bg-[rgba(66,87,255,0.22)]",
    icon: "bg-marketing-lavender text-marketing-purple",
  },
  {
    card: "bg-[linear-gradient(145deg,#fff_30%,#edfff7)] after:bg-[rgba(99,209,157,0.32)]",
    icon: "bg-[#E7FFB6] text-[#315E09]",
  },
  {
    card: "bg-[linear-gradient(145deg,#fff_30%,#fff0f7)] after:bg-[rgba(255,79,154,0.22)]",
    icon: "bg-[#FFE1EF] text-[#B01861]",
  },
];

/** Three numbered cards with arrows between them. */
export function StepCards({
  steps,
  className,
}: {
  steps: Step[];
  className?: string;
}) {
  return (
    <div
      className={cn(
        "mt-[52px] grid grid-cols-[minmax(0,1fr)_40px_minmax(0,1fr)_40px_minmax(0,1fr)] items-center gap-[10px] max-[980px]:grid-cols-1",
        className,
      )}
    >
      {steps.map((step, i) => (
        <Fragment key={step.title}>
          <article
            className={cn(
              "relative min-h-[315px] overflow-hidden rounded-[25px] border border-[#DFE3F1] p-[30px] shadow-[0_10px_40px_rgba(16,28,84,0.06)] after:absolute after:-bottom-[85px] after:-right-[75px] after:size-[150px] after:rounded-full after:blur-[4px] after:content-[''] max-[980px]:min-h-[220px]",
              CARD_STYLES[i % 3].card,
            )}
          >
            <div
              className={cn(
                "mb-8 grid size-12 place-items-center rounded-[14px] font-figtree text-[20px] font-black leading-none",
                CARD_STYLES[i % 3].icon,
              )}
            >
              0{i + 1}
            </div>
            <h3 className="m-0 mb-[10px] font-figtree text-[23px] font-[850] leading-[1.15]">
              {step.title}
            </h3>
            <p className="m-0 font-open-sans text-[16px] leading-[1.55] text-marketing-muted">
              {step.body}
            </p>
          </article>
          {i < steps.length - 1 && (
            <span
              aria-hidden
              className="text-center font-figtree text-[44px] leading-none text-[#8293D8] max-[980px]:rotate-90"
            >
              →
            </span>
          )}
        </Fragment>
      ))}
    </div>
  );
}

type HowItWorksProps = {
  title: ReactNode;
  intro?: ReactNode;
  steps: Step[];
  /** Shows the "How it works" kicker above the title. */
  kicker?: boolean;
};

export default function HowItWorks({
  title,
  intro,
  steps,
  kicker = true,
}: HowItWorksProps) {
  return (
    <MarketingSection id="how-it-works" className="bg-white">
      {kicker && <SectionKicker>How it works</SectionKicker>}
      <SectionTitle>{title}</SectionTitle>
      {intro && <SectionIntro>{intro}</SectionIntro>}
      <StepCards steps={steps} />
    </MarketingSection>
  );
}
